import { Alert, Box, Stack } from '@mui/material';
import { useState } from 'react';
import type { AdminTab } from '../adminSections';
import { isAdminGateOpen } from '../../utilities';
import AdminAccordionSection from './AdminAccordionSection';

interface AdminTabContentProps {
  tab: AdminTab;
  enabledItems: ReturnType<
    typeof import('../../utilities').getEnabledMenuItems
  >;
  // When false, items flagged `advanced: true` are hidden (and sections that
  // become empty as a result are hidden too).
  showAdvanced: boolean;
}

const AdminTabContent = ({
  tab,
  enabledItems,
  showAdvanced,
}: AdminTabContentProps) => {
  // Sections are multi-open. The parent stores the set of expanded section ids.
  // Default-expansion rules:
  //  - Single-section tab (e.g. User Access → Manage User Permissions): expand
  //    that section so the user doesn't have to click an obvious one-row
  //    accordion.
  //  - Platform Settings: expand both Module Visibility and Site Settings —
  //    they're short, frequently scanned together, and the page reads better
  //    open by default.
  //  - Program Settings: expand the Quick Actions shortcut row so the buttons
  //    are immediately visible (its whole purpose is at-a-glance access).
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (tab.sections.length === 1) return new Set([tab.sections[0].id]);
    if (tab.id === 'platform') return new Set(tab.sections.map((s) => s.id));
    if (tab.id === 'program') return new Set(['quick-actions']);
    return new Set();
  });

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  // Tab intentionally has no sections (e.g., Project Settings placeholder).
  if (tab.sections.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" variant="outlined">
          {tab.emptyPlaceholder ??
            `${tab.label} is currently empty — no admin tools are configured for this tab.`}
        </Alert>
      </Box>
    );
  }

  // Filter sections by permission AND the advanced toggle. A section with no
  // surviving visible items is hidden.
  const visibleSections = tab.sections
    .map((section) => {
      const visibleItemIds = new Set(
        section.items
          .filter((item) => isAdminGateOpen(enabledItems, item.permissionKey))
          .filter((item) => showAdvanced || !item.advanced)
          .map((i) => i.id),
      );
      return { section, visibleItemIds };
    })
    .filter(({ section, visibleItemIds }) => {
      if (visibleItemIds.size === 0) return false;
      return isAdminGateOpen(enabledItems, section.permissionKey);
    });

  if (visibleSections.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" variant="outlined">
          You do not currently have permission to view any sections of the{' '}
          {tab.label} tab. Once SPRINT-001 seeds the supporting Action rows and
          your role is updated, the sections will appear here.
        </Alert>
      </Box>
    );
  }

  return (
    <Stack spacing={0} sx={{ p: 2 }}>
      {visibleSections.map(({ section, visibleItemIds }) => (
        <AdminAccordionSection
          key={section.id}
          section={section}
          visibleItemIds={visibleItemIds}
          expanded={expandedSections.has(section.id)}
          onToggle={toggleSection}
        />
      ))}
    </Stack>
  );
};

export default AdminTabContent;
